import { useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import type { ReactNode, Ref } from 'react';
import { createPortal } from 'react-dom';
import { Modal } from '../bootstrapJs';

import type { ModalWidth } from '../types';

export type NgModalHandle = {
  show: () => void;
  hide: () => void;
};

export type NgModalProps = {
  header?: string;
  headerTemplate?: ReactNode;
  text?: string;
  bodyTemplate?: ReactNode;
  footerTemplate?: ReactNode;
  onShown?: () => void | Promise<void>;
  dynamicWidth?: boolean;
  centered?: boolean;
  disableClosingDialog?: boolean;
  width?: ModalWidth;
  testID?: string;
  ref?: Ref<NgModalHandle>;
};

const MODAL_WIDTH_CSS: Record<ModalWidth, string> = {
  small: 'modal-sm',
  regular: '',
  medium: 'modal-md',
  large: 'modal-lg',
  extraLarge: 'modal-xl',
};

export function NgModal({
  header,
  headerTemplate,
  text,
  bodyTemplate,
  footerTemplate,
  onShown,
  dynamicWidth = false,
  centered = false,
  disableClosingDialog,
  width = 'regular',
  testID,
  ref,
}: NgModalProps) {
  const modalElementRef = useRef<HTMLDivElement>(null);
  const headerId = useId();
  const [opened, setOpened] = useState(false);
  const shouldShowRef = useRef(false);
  const onShownRef = useRef(onShown);
  onShownRef.current = onShown;

  useImperativeHandle(ref, () => ({
    show: () => {
      shouldShowRef.current = true;
      setOpened(true);
    },
    hide: () => {
      const element = modalElementRef.current;
      if (element) {
        Modal.getInstance(element)?.hide();
      }
    },
  }));

  useEffect(() => {
    const element = modalElementRef.current;
    if (!element) {
      return;
    }

    const handleShown = () => {
      void onShownRef.current?.();
    };
    const handleHidden = () => {
      setOpened(false);
    };
    element.addEventListener('shown.bs.modal', handleShown);
    element.addEventListener('hidden.bs.modal', handleHidden);

    return () => {
      element.removeEventListener('shown.bs.modal', handleShown);
      element.removeEventListener('hidden.bs.modal', handleHidden);
      const instance = Modal.getInstance(element);
      if (!instance) {
        return;
      }
      const disposingWhileShownWouldLeaveBodyScrollLocked = element.classList.contains('show');
      if (disposingWhileShownWouldLeaveBodyScrollLocked) {
        element.addEventListener('hidden.bs.modal', () => instance.dispose(), { once: true });
        instance.hide();
      } else {
        instance.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (opened && shouldShowRef.current) {
      shouldShowRef.current = false;
      const element = modalElementRef.current;
      if (element) {
        Modal.getOrCreateInstance(element, { keyboard: !disableClosingDialog }).show();
      }
    }
  }, [opened, disableClosingDialog]);

  const modalSize = dynamicWidth ? 'ng-modal--dynamic-width' : '';
  const disableCloseButtonCss = disableClosingDialog ? 'ng-modal--disable-close-button' : '';
  const cssClass = `ng-modal ${MODAL_WIDTH_CSS[width]} ${modalSize} ${disableCloseButtonCss}`;

  return createPortal(
    <div
      ref={modalElementRef}
      className={`hx-modal modal fade ${cssClass}`}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headerId}
      data-bs-backdrop={disableClosingDialog ? 'static' : 'true'}
      data-testid={testID}
    >
      <div className={`modal-dialog modal-fullscreen-md-down${centered ? ' modal-dialog-centered' : ''}`}>
        {opened && (
          <div className="modal-content">
            <div className="modal-header">
              <h2 id={headerId}>{header}</h2>
              {headerTemplate}
              <button
                type="button"
                className="btn-close text-reset"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              {text}
              {bodyTemplate}
            </div>
            <div className="modal-footer">{footerTemplate}</div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
